import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/modal';
import './new-crisis-list-modal.scss';
import { ClientContactType, ReactionListRecipientType, ReactionListType } from 'types';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import api from 'api';

function NewCrisisListModal({
    open,
    setOpen,
    onSave
}: {
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>,
    onSave: () => void
}) {
    const params: any = useParams();
    const [reactionList, setReactionList] = useState<ReactionListType>({
        DateLastSent: new Date(),
        Description: '',
        Id: 0,
        IsActive: false,
        IsGeneric: false,
        Keywords: [],
        LanguageId: '',
        NotificationInterval: '',
        ProjectId: params.projectId,
        Recipients: [],
        SentimentId: 0,
        StartDate: new Date(),
        StopDate: new Date(moment().add(30, 'd').toString()),
        WordBinTypeId: 2,
        WordbinGroupId: 0
    });
    
    const [clientContacts, setClientContacts] = useState<ClientContactType[]>([]);
    const [newRecipient, setNewRecipient] = useState<ReactionListRecipientType | null>();
    const [newRecipientOpen, setNewRecipientOpen] = useState<boolean>(false);

    const getClientContacts = useCallback(async () => {
        const data = await api.clientContact.getAll(params.clientId)
        setClientContacts(data);
    }, [params.clientId])

    const handleAddRecipient = () => {
        setReactionList({
            ...reactionList,
            Recipients: [...reactionList.Recipients, newRecipient!]
        })
        setNewRecipient(null);
        setNewRecipientOpen(false);
    }

    const handleRemoveRecipient = (id: number) => {
        setReactionList({
            ...reactionList!,
            Recipients: reactionList!.Recipients.filter(x => x.ContactId !== id)
        })
    }

    const handleSelectContact = (id: number) => {
        const contact = clientContacts.find(x => x.Id === id);
        setNewRecipient({
            Cellphone: contact!.Phone,
            ContactId: contact!.Id,
            Email: contact!.Email,
            Name: contact!.Name,
            Surname: contact!.Surname,
            WordBinRecipientTypeId: 0
        });
    }

    const getWordbinRecipientTypeString = useCallback((id: number) => {
        switch (id) {
            case 1:
                return "Alert Contact";
            case 2:
                return "Popup Contact";
            case 3:
                return "Response Contact"
            default:
                return null;
        }
    }, [])

    const OnSave = (async () => {

        await api.reactionLists.save({
            AccountId: 0,
            ClientId: params.clientId,
            Description: reactionList.Description,
            Id: 0,
            IsGeneric: reactionList.IsGeneric,
            Keywords: reactionList.Keywords,
            LanguageId: reactionList.LanguageId,
            NotificationInterval: Number(reactionList.NotificationInterval),
            ProjectId: params.projectId,
            Recipients: reactionList.Recipients,
            SentimentId: reactionList.SentimentId,
            StartDate: reactionList.StartDate,
            StopDate: reactionList.StopDate,
            WordBinTypeId: 2
        })

        setOpen(!open)
    })

    useEffect(() => {
        getClientContacts();
    }, [getClientContacts])

    return (
        <Modal open={open} setOpen={setOpen} id="new-reaction-list-modal">
            <ModalHeader>Add Crisis List</ModalHeader>
            <ModalBody>
                <div className="input-group">
                    <label>Name</label>
                    <input type="text" placeholder="Name" value={reactionList.Description} onChange={e => setReactionList({ ...reactionList, Description: e.currentTarget.value })} />
                </div>
                <div className="input-group">
                    <label>Notify Me</label>
                    <select value={reactionList.NotificationInterval} onChange={e => setReactionList({ ...reactionList, NotificationInterval: e.currentTarget.value })}>
                        <option value="" disabled>Choose option</option>
                        <option value="0">Immediate</option>
                        <option value="5">Every 5 minutes</option>
                        <option value="10">Every 10 minutes</option>
                        <option value="20">Every 20 minutes</option>
                        <option value="30">Every 30 minutes</option>
                        <option value="60">Every Hour</option>
                        <option value="360">Every Six Hours</option>
                        <option value="720">Every Twelve Hours</option>
                        <option value="1440">Once a day</option>
                    </select>
                </div>
                <div className="input-group">
                    <label>Sentiment</label>
                    <select value={reactionList.SentimentId} onChange={e => setReactionList({ ...reactionList, SentimentId: parseInt(e.currentTarget.value) })}>
                        <option value="0">None</option>
                        <option value="1">Neutral</option>
                        <option value="2">Positive</option>
                        <option value="3">Negative</option>
                    </select>
                </div>
                <div className="input-group">
                    <label>Start Date</label>
                    <input type="date" id="start-date" placeholder="YYYY-MM-DD" value={moment(reactionList.StartDate).format("YYYY-MM-DD")} onChange={e => setReactionList({ ...reactionList, StartDate: new Date(e.currentTarget.value) })} />
                </div>
                <div className="input-group">
                    <label>End Date</label>
                    <input type="date" id="end-date" placeholder="YYYY-MM-DD" value={moment(reactionList.StopDate).format("YYYY-MM-DD")} onChange={e => setReactionList({ ...reactionList, StopDate: new Date(e.currentTarget.value) })} />
                </div>
                <div className="input-group">
                    <label>Recipients</label>
                    {reactionList.Recipients.map(x => (
                        <div className="recipient">
                            <span className="type">{getWordbinRecipientTypeString(x.WordBinRecipientTypeId)}</span>
                            <span className="contact">{x.Name} {x.Surname}</span>
                            <i className="fal fa-times" onClick={() => handleRemoveRecipient(x.ContactId)}></i>
                        </div>
                    ))}
                    <div className={`recipients ${newRecipientOpen ? 'open' : ''}`}>
                        <select value={newRecipient?.ContactId || ''} onChange={e => handleSelectContact(parseInt(e.currentTarget.value))}>
                            <option value="" disabled selected>Select Contact</option>
                            {clientContacts.filter(x => !reactionList.Recipients.map(r => r.ContactId).includes(x.Id)).map(x => (
                                <option value={x.Id} key={x.Id}>{x.Name} {x.Surname}</option>
                            ))}
                        </select>
                        <select value={newRecipient?.WordBinRecipientTypeId || 0} onChange={e => setNewRecipient({ ...newRecipient!, WordBinRecipientTypeId: parseInt(e.currentTarget.value) })}>
                            <option value="0" disabled selected>Select Type</option>
                            <option value="1">Alert Contact</option>
                            <option value="2">Popup Contact</option>
                            <option value="3">Response Contact</option>
                        </select>
                    </div>
                    {newRecipientOpen && <button className="btn-save" onClick={handleAddRecipient}>Save</button>}
                    <button onClick={() => setNewRecipientOpen(!newRecipientOpen)}>{newRecipientOpen ? 'Cancel' : 'Add'}</button>
                </div>
            </ModalBody>
            <ModalFooter>
                <button onClick={OnSave}>Save</button>
            </ModalFooter>
        </Modal>
    )
}

export default NewCrisisListModal;